import { API } from "@swivl-mono/api-helper";
import { formatTicketNumberDisplay } from "@swivl-mono/types";
import { Button, ButtonColor, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiCheck } from "react-icons/fi";

import { Member } from "../../../../Models/Member/Member.Current";
import { PolicyFindingsReview } from "../../../Admin/DocumentIngest/PolicyFindingsReview";
import { FmsStep } from "./FmsStep";
import { GoogleStep } from "./GoogleStep";
import { HoursStep } from "./HoursStep";
import { StepOutcome, WizardProgress } from "./WizardChrome";

type WizardStepKey = "fms" | "hours" | "policies" | "google";

const STEP_LABELS: Record<WizardStepKey, string> = { fms: "FMS", hours: "Hours", policies: "Policies", google: "Google" };

interface AddLocationWizardProps {
  locationId: number;
  groupId: number;
  // Scrape/upload source with policy findings, or null for a manual add (no policies step).
  reviewSourceId: number | null;
  onClose: () => void;
  onDone: () => void;
}

// Post-create guided setup: FMS -> hours -> policies (if any) -> Google, backed by an onboarding ticket.
export function AddLocationWizard({ locationId, groupId, reviewSourceId, onClose, onDone }: AddLocationWizardProps): React.ReactElement {
  const steps: WizardStepKey[] = reviewSourceId != null ? ["fms", "hours", "policies", "google"] : ["fms", "hours", "google"];
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<number | null>(null);
  // Label index -> outcome; "Details" (0) is already done. Drives the rail icons.
  const [outcomes, setOutcomes] = useState<Map<number, StepOutcome>>(new Map([[0, "completed"]]));
  // Drafts live here so field input survives Back/Skip between steps.
  const [fmsId, setFmsId] = useState("");
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [googleUrl, setGoogleUrl] = useState("");

  // On open: derive progress (start at the first incomplete step, seed the rail) and ensure the ticket.
  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        const progress = await API.Onboarding.getProgress(Member.orgId, locationId, reviewSourceId ?? undefined);
        const seeded = new Map<number, StepOutcome>([[0, "completed"]]);
        progress.steps.forEach((step, i) => step.done && seeded.set(i + 1, "completed"));
        setOutcomes(seeded);
        if (progress.firstIncompleteIndex === -1) setFinished(true);
        else if (progress.firstIncompleteIndex > 0) setIndex(progress.firstIncompleteIndex);
        if (Member.id != null) {
          const { ticketNumber } = await API.Onboarding.ensureTicket(Member.orgId, locationId, Member.id, reviewSourceId ?? undefined);
          setTicketNumber(ticketNumber);
        }
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, [locationId, reviewSourceId]);

  // Re-sync the ticket's task states from current data (best-effort).
  const syncTicket = (): void => {
    if (Member.id != null) void API.Onboarding.ensureTicket(Member.orgId, locationId, Member.id, reviewSourceId ?? undefined);
  };

  const step = steps[index];
  const isLast = index === steps.length - 1;

  const goNext = (outcome: StepOutcome): void => {
    setOutcomes((prev) => new Map(prev).set(index + 1, outcome));
    syncTicket();
    if (isLast) {
      setFinished(true);
    } else {
      setIndex(index + 1);
    }
  };
  const goBack = index > 0 ? () => setIndex(index - 1) : undefined;
  const handleClose = (): void => {
    syncTicket();
    onClose();
  };
  const labels = ["Details", ...steps.map((key) => STEP_LABELS[key])];

  if (loading) {
    return (
      <div className="flex h-40 w-[640px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (finished) {
    return (
      <div className="flex w-[640px] flex-col items-center gap-3 px-6 py-10 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-teal-100 text-teal-700">
          <FiCheck className="size-6" />
        </div>
        <h3 className="font-poppins text-xl font-semibold text-grey-900">Onboarding set up</h3>
        <p className="body-sm max-w-md text-grey-900">
          swivl automated what it could from your website - only your team knows the rest. Finish up in{" "}
          <a
            href={`/properties-v3/group/${groupId}/location/${locationId}/page/details-v3/facility-info`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-orange-500 hover:text-orange-600 hover:underline"
          >
            Details
          </a>{" "}
          and{" "}
          <a
            href={`/properties-v3/group/${groupId}/location/${locationId}/page/policy-worksheets`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-orange-500 hover:text-orange-600 hover:underline"
          >
            Policies
          </a>{" "}
          so your bot answers with full, accurate info.
        </p>
        {ticketNumber != null && (
          <a
            href={`/inbox/ticket/${formatTicketNumberDisplay(ticketNumber)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-orange-500 hover:text-orange-600 hover:underline"
          >
            View the onboarding ticket →
          </a>
        )}
        <Button color={ButtonColor.ORANGE} onClick={onDone}>
          Go to location
        </Button>
      </div>
    );
  }

  return (
    <div className="flex max-h-[75vh] w-[640px] flex-col">
      <WizardProgress labels={labels} current={index + 1} outcomes={outcomes} onSelect={(labelIndex) => setIndex(labelIndex - 1)} />
      {step === "fms" && (
        <FmsStep
          locationId={locationId}
          groupId={groupId}
          fmsId={fmsId}
          onFmsId={setFmsId}
          integrationId={integrationId}
          onIntegrationId={setIntegrationId}
          onBack={goBack}
          onClose={handleClose}
          onNext={goNext}
        />
      )}
      {step === "hours" && (
        <HoursStep locationId={locationId} groupId={groupId} sourceId={reviewSourceId} onBack={goBack} onClose={handleClose} onNext={goNext} />
      )}
      {step === "policies" && reviewSourceId != null && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-1 pt-3">
            <PolicyFindingsReview
              sourceId={reviewSourceId}
              locationId={locationId}
              finishLabel="Approve"
              onFinish={() => goNext("completed")}
              onBack={goBack}
              onClose={handleClose}
            />
          </div>
        </div>
      )}
      {step === "google" && (
        <GoogleStep
          locationId={locationId}
          groupId={groupId}
          url={googleUrl}
          onUrl={setGoogleUrl}
          onBack={goBack}
          onClose={handleClose}
          onNext={goNext}
        />
      )}
    </div>
  );
}
